/**
 * name: petails-wizard-js
 * version: 3.11.1
 * created: 2026-08-16
 * updated: 2026-10-28
 * author: Raffaello + Claude (AI-assisted)
 * status: active
 * purpose: Wizard logic - 5-screen intake, lead capture, hardened cart-to-checkout handoff.
 * deps: [petails-wizard-section >= 4.3.0, petails-r2-upload]
 * changelog:
 * - [3.11.1] 2026-10-28 (Raffaello + Claude): The two headings built here are h2, not h1
 *   (SEO: one h1 per page). Needs css >= 4.9.1.
 * - [3.11.0] 2026-10-01 (Raffaello + Claude): The last screen now ends. (1) Success switches to
 *   the new #pf-up-done view - a tick, "Check your email", the address, a Back to home button -
 *   instead of leaving the spinning orb and the progress bar on screen with a changed status
 *   line, which is why a finished submission looked stuck. (2) The customer is sent to / after
 *   HOME_DELAY_S seconds, with a visible countdown and a button for the impatient; goHome()
 *   clears the beforeunload guard first so nothing asks them to confirm. (3) SUBMIT_DEADLINE_MS
 *   caps the whole submission at 45s - photos (3x20s) then meta (3x20s) could spin for over two
 *   minutes before showing anything. On deadline the error view says plainly that we could not
 *   confirm, and retrying is safe because the sheet upsert keys on submissionId.
 * - [3.10.0] 2026-09-23 (Raffaello + Claude): Typing is fixed. (1) Textareas auto-grow to the
 *   sentence (autoGrow, ceiling 340px then scroll) instead of scrolling inside a fixed 88px
 *   box - a scrolled textarea is where iOS drops the caret. (2) Per-keystroke work is
 *   coalesced: paint updates run once per frame (scheduleRefresh) and localStorage writes
 *   450ms after typing stops (scheduleSave); before, every character forced a synchronous
 *   reflow plus a JSON.stringify + localStorage write. (3) measureHead ignores resize while a
 *   field has focus, so the mobile keyboard no longer shifts scroll-margin mid-sentence.
 *   (4) Proper keyboard hints per field (autocapitalize words/sentences, autocomplete,
 *   spellcheck, enterkeyhint) and Enter moves to the next field on the card instead of doing
 *   nothing. buildFormData and every contract 4 surface untouched.
 * - [3.9.0] 2026-09-22 (Raffaello + Claude): verifiedCartAdd now asserts the cart holds
 *   EXACTLY the items it just added (count AND variant ids) instead of "at least one".
 *   A silent /cart/clear.js failure used to let a browser's accumulated cart ride along to
 *   checkout, where the customer could not edit it (no /cart page). Same fix as section
 *   4.7.0 applies to the JS fallback bridge. CONTRACT 4 untouched: bridge URL shape,
 *   submissionId line-item property, endpoints and variant ids are all identical.
 * - [3.8.0] 2026-09-21 (Raffaello + Claude): The real photo no longer travels in the marquee:
 *   showPreview pins it into #pv-orig (hidden until it loads, so never an empty frame) and the
 *   track carries only the example pages. Display only; no contract surface.
 * - [3.7.0] 2026-09-21 (Raffaello + Claude): Submission can no longer hang for minutes.
 *   (1) saveMeta treats success:true as done even when notified:false - the Worker already
 *   retried the Sheets webhook 3x server-side and meta.json is durably in R2; the browser
 *   re-posting only held the customer hostage (this was the "never finishes" screen, root
 *   cause: the deployed Apps Script answering {success:false,error:"unauthorized"} to the
 *   Worker since ~2026-09-20 13:00 UTC - backend fix owed by Book Factory). (2) Timeouts
 *   20s / attempts 3 for photos and meta: worst case ~60s to a clear retry screen, not ~2 min
 *   of frozen "Saving". (3) After 18s a "still working" line replaces the static status.
 *   buildFormData untouched.
 * - [3.6.0] 2026-09-21 (Raffaello + Claude): The flow ENDS on the studio screen. selectProduct
 *   no longer builds a cart or redirects to checkout; on success it shows "All saved" and
 *   stays. No rescue link, no pendingProductUrl. isSubmitting stays true after success so a
 *   second send is impossible from the same session. goToCheckout/verifiedCartAdd untouched -
 *   the upgrade bridge (contract 4) still uses them. Book Factory note: the wizard now creates
 *   ZERO Shopify orders; all payment starts from the portal/bridge, so the pending_checkout ->
 *   received flip only ever happens on bridge purchases.
 * - [3.5.0] 2026-09-21 (Raffaello + Claude): (1) One product: every submission checks out as
 *   Digital 51850659463453 - format cards and data.format removed (Book Factory note: no
 *   hardcover-first orders come from the wizard any more; hardcover is portal upsell only).
 *   (2) Age ranges no longer overlap: 0-3 / 4-6 / 7-9 / 10-12 / 13+ (values change - Book
 *   Factory vocabulary note). (3) Stories: required = any non-empty answer; the 25-char FLOOR
 *   is gone from validation AND from the meter (score is now answered/not, so the meter and
 *   Next always agree - "Jump to pool" passes). (4) Universe preview is an auto-scrolling
 *   marquee: one real photo then the pages, looping, no labels, no swipe needed. (5) Studio
 *   screen: buildConfirm removed; the customer's email is injected into #confirm-email.
 *   buildFormData untouched; submissionId, endpoints, bridge all as in contract 4.
 * - [3.4.0] 2026-09-16 (Raffaello + Claude): Format is chosen on the universe screen.
 *   data.format ('digital'|'hardcover') maps to FORMAT_VARIANTS (51850659463453 / 51850659528989
 *   - both already in contract 4); Create my book carries the selected variant instead of
 *   hardcoding hardcover. Stage 4 is one sub (vibe + format + occasion, all validated with
 *   .miss marks). Source question and newsletter row removed from the UI: data.source stays ''
 *   and data.newsletter now defaults FALSE (consent moves to Shopify checkout's own marketing
 *   checkbox - never silently true). buildFormData untouched; keys travel as before.
 *   Book Factory note: source arrives empty; newsletter arrives false unless checkout consent
 *   is wired. Build screen: buildConfirm() fills #build-rows with format-specific promises
 *   (digital: inbox <24h / 1 revision / login+upgrade · hardcover: preview first / approve
 *   before print / 5-7 days) and #pf-film-strip with an ambient marquee of the chosen
 *   universe's example art (same URLs as screen 4 - already cached, zero new weight).
 * - [3.3.0] 2026-09-16 (Raffaello + Claude): Universe examples open with the customer's REAL
 *   uploaded photos (before -> after proof). r2photos() builds photo_NN.jpg URLs from the
 *   CONTRACT-03 R2 layout; each VIBES entry carries orig:[2 photos of the same book the
 *   example pages come from]; showPreview prepends them as .page.orig tiles plus a
 *   'became this ->' divider. A photo that fails to load removes its own tile (and the
 *   divider when both fail) - never an empty frame. Read-only display; no contract surface.
 * - [3.2.0] 2026-09-16 (Raffaello + Claude): Age is five range chips (0-3 / 3-5 / 5-8 / 8-12 /
 *   12+), one tap, plain single-select. The coat colour question is REMOVED - the photos carry
 *   the colour; data.color stays in buildFormData as '' so the sheet column keeps its position
 *   (same treatment as zip). Book Factory note: color now arrives empty. Dead range/swatch
 *   branches removed. buildFormData untouched.
 * - [3.1.0] 2026-09-16 (Raffaello + Claude): (1) Age is 26 tap-chips (<1..25+), no slider - a
 *   thumb-drag is a precision task on a phone; stores the same 'N years' strings. (2) Coat is
 *   colour-only multi-select; the picks travel JOINED in the same single column ('Black,
 *   White') so the sheet shape is untouched. (3) A failed Next marks every unanswered field
 *   in the error colour (.miss) and lands on the first; any interaction clears the marks.
 *   (4) The optional card's button reads 'Skip for now' until something is typed. No
 *   contract 4 surface.
 * - [3.0.0] 2026-09-16 (Raffaello + Claude): BREAKING - needs section >= 4.0.0. One product:
 *   Next on stage 4/5 fires the submission (selectProduct with hardcover 51850659528989) and
 *   stage 5/5 is the build screen that hands off to checkout; the format screen, the digital
 *   offer and fmtFill are gone. KNOWN COST, decided by Raffaello 2026-09-16: save_meta now
 *   fires for every completed form, so received rows and Make runs exist for non-buyers.
 *   Meter reweighted 80/20 (4 required answers = 80%, light green; 100% dark green #3E7A46),
 *   tick moves to 80, slim meter lives in the nav and shows only on stage 3/5. Age is a 0-25
 *   slider (stores 'N years' / 'under 1 year' / '25+ years'); coat is an 11-swatch palette
 *   (stores the label text). Same columns, fixed vocabularies instead of free text - Book
 *   Factory note. buildFormData and all frozen plumbing byte-identical, as ever.
 * - [2.0.0] 2026-09-16 (Raffaello + Claude): BREAKING - needs section >= 3.0.0 and css >= 3.0.0.
 *   Navigation gains a sub-step layer. Each of the five stages is now a short series of cards
 *   shown one at a time, so a phone never scrolls a whole stage: stage 1 is photos / pet / you,
 *   stage 2 is one card per block, stage 3 is one required question per card plus the optional
 *   set, stage 4 is world / occasion. The rail segment fills by --segPct as cards advance, so
 *   moving inside a stage still reads as progress. Auto-advance on a single tap was built and
 *   then removed: measured against the real cards it never fired (every one holds a text field
 *   or a multi-select), and the one card where it could fire would have skipped two optional
 *   questions. 'Their world' splits into 'Their day' + 'Their world' - 9 fields on one card
 *   measured 1135px of scroll at 390x780, which is the thing this release exists to remove;
 *   no key changes, only how they are grouped. The block accordion, the required collapse bar
 *   and the optional CTA are gone: they existed to manage scroll, which cards now remove.
 *   New welcome card states the promise and the one rule before the first field. Progress now
 *   stores the card as well as the stage. UNCHANGED: every field id, buildFormData, the photo
 *   pipeline, save_lead/save_meta, checkout and the upgrade bridge. No contract 4 surface.
 * - [1.7.1] 2026-09-15 (Raffaello + Claude): 'The energy' asks its 14-chip multi-select in the
 *   middle: energy, traits, social. Presentation only - BLOCKS drives render order, while
 *   buildFormData writes its keys in a hand-written literal, so no spreadsheet column moves.
 * - [1.7.0] 2026-09-15 (Raffaello + Claude): Cinema Poster has examples. r2pages() builds them
 *   from the layout CONTRACT-03 fixes - {submissionId}/composed/page_NN.png on the R2 public
 *   base - so a universe no longer waits on someone hand-uploading pages to Shopify Files.
 *   Verified against the live objects before shipping: 12 of 12 probes loaded. Wired to
 *   sub_1788609536_p099 (Sasha Marie Lewis, universe cinema, ready, 53 pages), pages
 *   0/11/15/20/31 - caption pages deliberately skipped. Separately, the 20 Shopify-CDN URLs
 *   now carry &width=620: the same page measured 134KB instead of 874KB, on the most
 *   image-heavy screen of the form. R2 cannot resize, so the five cinema pages ship full
 *   size until they are moved to the CDN. No plumbing, no contract 4 surface.
 * - [1.6.0] 2026-09-15 (Raffaello + Claude): placePreview removed. It moved the example panel
 *   INTO the universe grid, after the tapped card's row, which split the five selectors in two
 *   - on a phone the last two ended up below the examples and out of sight. The panel now stays
 *   where the section puts it: under all five, always. No plumbing, no contract 4 surface.
 * - [1.5.0] 2026-09-15 (Raffaello + Claude): The required half folds itself into its pink bar
 *   the moment all four clear the floor, and unfolds if an answer is emptied - transition-only,
 *   so a manual re-open is never undone. New --meterH, measured from the sticky screen-3 meter,
 *   so opening the optional section lands its first question BELOW the meter instead of behind
 *   it. Meter caption rewritten around uniqueness rather than progress, and the 60%% tick is
 *   dropped from the UI once passed. No plumbing and no contract 4 surface touched.
 * - [1.4.0] 2026-09-15 (Raffaello + Claude): Reward between steps is back, as a toast above the
 *   nav plus a pop on the rail segment just completed - not the full-screen card that cost a
 *   tap per step. Going back never celebrates. The optional CTA now shows what it is still
 *   worth (+N%, 'maxed' at zero), computed from the same DEPTH scoring as the meter. A block
 *   that auto-opens scrolls to 'start' so it clears the sticky header. The example strip is
 *   titled '<universe> Example'. No plumbing and no contract 4 surface touched.
 * - [1.3.2] 2026-09-15 (Raffaello + Claude): buildVibes emits name + subtitle only; the cover
 *   thumbnail is gone from the screen-4 selectors. VIBES[].cover is still read by fmtFill for
 *   the screen-5 plate, so the data table is unchanged. Markup only.
 * - [1.3.1] 2026-09-15 (Raffaello + Claude): The two generated field wrappers emit 'wz-field'
 *   instead of 'field', matching the section and the sheet - Dawn's base.css owns .field.
 *   Two string literals; no logic, no plumbing, no contract 4 surface touched.
 * - [1.3.0] 2026-09-15 (Raffaello + Claude): 9 chapters -> 5 screens (prototype
 *   petails-story-builder v1.4.0). Screens 2 and 3 are rendered from the BLOCKS/CORE/DEPTH
 *   tables below, so every chip value that feeds buildFormData has exactly one source.
 *   New: richness meter, accordion blocks with completion counters, per-question ideas,
 *   style preview strip, format plate. Removed: celebrations, notice screen, intro screen,
 *   hardcover quantity stepper. STORAGE_KEY bumped to v3 so a half-finished 9-chapter
 *   session cannot resume into a 5-screen wizard. UNTOUCHED, byte for byte: buildFormData
 *   (key names AND order, zip included), normalizePhoto + HEIC + EXIF, uploadAllPhotos,
 *   The ONLY edit inside the frozen plumbing is the X-Petails-Secret header value, which now
 *   reads PW_KEY from assets/petails-wizard-key.js instead of an inline literal - same header,
 *   same token, same tier. Three lines, nothing else.
 *   saveMeta, finalizeSubmission, goToCheckout/verifiedCartAdd, sendLead, getSubmissionId.
 *   Contract 4: no key, no submissionId format, no endpoint, no variant id changes. Five
 *   columns that used to be required now arrive optional (essence, monologue, mishaps,
 *   companions, quirks) - Book Factory must tolerate empty strings there. zip was already
 *   optional and is no longer asked at all.
 * - [1.2.0] 2026-09-15 (Raffaello + Claude): Lead capture (Fase A, contract v1.1.0) - owner
 *   name/email move to chapter 1; leaving chapter 1 fires save_lead + background photo upload;
 *   submissionId and uploaded photo URLs persist in localStorage so a return visit reuses the
 *   same id and re-uploads nothing; first-touch UTM/fbclid/referrer captured on load and sent
 *   on the lead. buildFormData, save_meta, goToCheckout and the upgrade bridge are UNTOUCHED.
 * - [1.1.0] 2026-09-08 (Raffaello + Claude): goToCheckout hardened - verifies /cart/add.js response and real cart contents before navigating (fetch does not throw on 4xx/5xx), one retry, permalink fallback only as last resort; rescue link on the handoff screen. Fixes silent empty-cart checkout stranding paying customers.
 * - [1.0.0] 2026-08-16 (Raffaello + Claude): Byte-identical port of the monolith wizard JS to a theme asset (migration guide App. D).
 */

  const TOTAL_SCREENS = 5;               // 0..4 are content; 5 is the saving/handoff screen
  const STORAGE_KEY = 'thepetails_wizard_v3';
  const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
  let currentStep = 0;
  const data = {
    photos: [],
    name: '', species: '', breed: '', age: '', weight: '', color: '', gender: '',
    energy: '', social: '', traits: [], essence: '', signature: '', monologue: '',
    feeding: '', treats: '', activities: [], sleep: '', quirks: '',
    city: '', zip: '', home: '', squad: [], humans: '', weekend: [],
    enemies: '', iconic: '', mishaps: '', companions: '', oneThingToKnow: '',
    vibe: '', creativeDirection: '',
    ownerName: '', ownerEmail: '', occasion: '', source: '', newsletter: false
  };

  // ── First-touch attribution (lead-capture contract v1.1.0 §6) ──
  // Captured once at page load, persisted with progress, sent on the lead row.
  // Kept OUTSIDE `data` so it can never leak into buildFormData or the sheet.
  let attrib = { utm_source: '', utm_campaign: '', fbclid: '', referrer: '' };
  try {
    const q0 = new URLSearchParams(location.search);
    attrib = {
      utm_source: q0.get('utm_source') || '',
      utm_campaign: q0.get('utm_campaign') || '',
      fbclid: q0.get('fbclid') || '',
      referrer: document.referrer || ''
    };
  } catch (e) { /* attribution is best-effort */ }

  // ══════════════════════════════════════════════════════════════════
  // SCREEN CONTENT — the single source for every value that reaches the
  // sheet. A chip's data-val is written once, here. Changing a `k` below
  // renames a spreadsheet column: see contract §4 before touching one.
  // ══════════════════════════════════════════════════════════════════
  const STEP_LABELS = ['The casting', 'Who they are', 'The stories', 'The universe', 'Your book'];

  const SPECIES = [['dog','🐶 Dog'],['cat','🐱 Cat'],['rabbit','🐰 Rabbit'],['bird','🦜 Bird'],['horse','🐴 Horse'],['reptile','🦎 Reptile'],['other','✨ Other glorious creature']];
  const OCCASION = [['birthday','🎂 Birthday'],['adoption','🎉 Adoption-versary'],['memorial','🕊️ In memory'],['holiday','🎄 Holiday gift'],['surprise','🎁 Surprise gift'],['none','— Just love']];
  // SOURCE question removed from the UI (3.4.0): Meta traffic is already attributed by UTM
  // in the D1 lead row; data.source travels as '' to keep its sheet column (like zip/color).

  // `cover` is the style's card art; `pages` are real composed pages from real books.
  //
  // Two sources, on purpose:
  //  · Shopify CDN + &width= — 134KB per page instead of 874KB, measured. Preferred.
  //  · R2, via r2pages() — the pipeline's own output, at the layout CONTRACT-03 fixes as
  //    {submissionId}/composed/page_NN.png (00-52). Nothing has to be uploaded by hand, but
  //    r2.dev cannot resize, so these ship as full-size PNGs. Use it to unblock a universe
  //    that has no pages on the CDN yet, then move it across and delete the r2pages() call.
  const R2_BASE = 'https://pub-e4683c248ac94c91a1a5eaea0573440e.r2.dev';
  function r2pages(submissionId, picks) {
    return picks.map(function (n) {
      return R2_BASE + '/' + submissionId + '/composed/page_' + String(n).padStart(2, '0') + '.png';
    });
  }
  // The customer's ORIGINAL uploads for the same books - the "before" of the proof.
  // CONTRACT-03 layout: {submissionId}/photo_NN.jpg (1-based). URLs verified against the
  // photo_urls column of each submission row. Full-size JPGs (r2.dev cannot resize) -
  // if they feel heavy on 4G, re-upload the chosen two per book to the Shopify CDN with
  // &width=320 and swap the URLs here; orig[] takes any absolute URL.
  function r2photos(submissionId, picks) {
    return picks.map(function (n) {
      return R2_BASE + '/' + submissionId + '/photo_' + String(n).padStart(2, '0') + '.jpg';
    });
  }
  const VIBES = [
    { id:'comic', name:'Comic Caper', sub:'Superhero · bold & loud',
      art:'linear-gradient(140deg,#FF3D6E,#FFC93C)',
      cover:'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/01-comic-caper_jpg.png?v=1778206140',
      desc:'Bold black outlines, halftone dots, screaming primary colours.',
      orig: r2photos('sub_1784070882_8qpe', [1, 2]),
      pages:[
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784070882_8qpe_composed_page_00.png?v=1789484738&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784070882_8qpe_composed_page_15.jpg?v=1789484771&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784070882_8qpe_composed_page_23.jpg?v=1789484770&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784070882_8qpe_composed_page_35.jpg?v=1789484770&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784070882_8qpe_composed_page_28.jpg?v=1789484770&width=620'
      ] },
    { id:'storybook', name:'Storybook Magic', sub:'Watercolor · gentle',
      art:'linear-gradient(140deg,#95C18C,#FFF4E0)',
      cover:'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/02-storybook-magic_jpg.png?v=1778206140',
      desc:'Soft watercolour washes, paper texture, golden-hour light.',
      orig: r2photos('sub_1787759308_tnfx', [1, 2]),
      pages:[
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1787759308_tnfx_composed_page_00.png?v=1789484346&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1787759308_tnfx_composed_page_27.jpg?v=1789484346&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1787759308_tnfx_composed_page_35.jpg?v=1789484346&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1787759308_tnfx_composed_page_20.jpg?v=1789484346&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1787759308_tnfx_composed_page_31.jpg?v=1789484347&width=620'
      ] },
    { id:'movie', name:'Cinema Poster', sub:'Cinematic · dramatic',
      art:'linear-gradient(140deg,#2B1A47,#FF3D6E)',
      cover:'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/03-cinema-poster_jpg.png?v=1778206140',
      desc:'Cinematic digital painting — dramatic shadows, rim light, epic energy.',
      // Sasha Marie Lewis · "Shadow Protocol" — cover, the bell, the doorway light, the
      // laundry-room chaos, the cat charge. Pages 5/8/18 are caption pages, not illustrations.
      orig: r2photos('sub_1788609536_p099', [1, 2]),
      pages: r2pages('sub_1788609536_p099', [0, 11, 15, 20, 31]) },
    { id:'heritage', name:'Heritage Edition', sub:'Oil portrait · keepsake',
      art:'linear-gradient(140deg,#1A1428,#FFC93C)',
      cover:'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/04-heritage-edition_jpg.png?v=1778206273',
      desc:'Classical oil portrait, Rembrandt lighting, museum-grade.',
      orig: r2photos('sub_1783691524_2377', [1, 2]),
      pages:[
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1783691524_2377_composed_page_00.png?v=1789485077&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1783691524_2377_composed_page_11.jpg?v=1789485078&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1783691524_2377_composed_page_15.jpg?v=1789485078&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1783691524_2377_composed_page_20.jpg?v=1789485078&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1783691524_2377_composed_page_12.jpg?v=1789485078&width=620'
      ] },
    { id:'pixar', name:'Pixar Magic', sub:'3D render · soulful',
      art:'linear-gradient(140deg,#FFC93C,#95C18C)',
      cover:'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/05-pixar-magic_jpg.png?v=1778206140',
      desc:'Full 3D render — big soulful eyes, warm light, real depth.',
      orig: r2photos('sub_1784552499_5gh7', [1, 2]),
      pages:[
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784552499_5gh7_composed_page_00.png?v=1789484861&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784552499_5gh7_composed_page_39.jpg?v=1789484861&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784552499_5gh7_composed_page_36.jpg?v=1789484861&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784552499_5gh7_composed_page_31.jpg?v=1789484861&width=620',
        'https://cdn.shopify.com/s/files/1/0992/4395/0365/files/sub_1784552499_5gh7_composed_page_32.jpg?v=1789484862&width=620'
      ] }
  ];
  const VIBE_LABEL = { comic:'Comic Caper', storybook:'Storybook Magic', movie:'Cinema Poster', heritage:'Heritage Edition', pixar:'Pixar Magic' };

  const BLOCKS = [
    { t:'The body', h:'The <em>basics</em>.', lede:'Four quick answers and we know who we are drawing.', fields:[
      { k:'breed',  type:'text',   lab:'What breed is {n}?', ph:'Frenchie · 100% mystery mix' },
      { k:'age',    type:'single', lab:'How old is {n}?', opts:[['0–3 years','0–3'],['4–6 years','4–6'],['7–9 years','7–9'],['10–12 years','10–12'],['13+ years','13+']] },
      { k:'weight', type:'single', lab:'How big is {n}?', opts:[['tiny','🐭 Pocket-sized'],['small','🐕 Small'],['medium','🐶 Medium'],['large','🐕‍🦺 Big boi'],['giant','🦁 Absolute unit']] },
      { k:'gender', type:'single', lab:'Do you call {n} he or she?', opts:[['m','He'],['f','She']] }
    ]},
    { t:'The energy', h:'What is their <em>whole deal</em>?', lede:'Be honest. No judgment.', fields:[
      { k:'energy', type:'single', lab:'How much energy does {n} have?', opts:[['couch','🛋️ Couch potato'],['chill','😌 Chill'],['playful','🎾 Playful'],['turbo','🚀 Turbo'],['chaos','😈 Chaos demon']] },
      { k:'traits', type:'multi',  lab:'How would you describe {n}?', opts:[['brave','😤 Brave'],['dramatic','🎭 Dramatic'],['sleepy','😴 Sleepy'],['foodie','🍔 Foodie'],['mischievous','😈 Mischievous'],['cuddly','🥰 Cuddly'],['athletic','🏃 Athletic'],['jealous','😤 Jealous'],['vocal','🗣️ Vocal'],['philosopher','🧘 Philosopher'],['elegant','🎩 Elegant'],['clown','🤡 House clown'],['diva','💅 Diva'],['anxious','😰 Anxious']] },
      { k:'social', type:'single', lab:'How is {n} with strangers?', opts:[['loves','🤗 Loves everyone'],['picky','👑 Selective royalty'],['shy','🫣 Shy bean'],['suspicious','🕵️ Suspicious of all'],['barks','📢 Barks at leaves']] }
    ]},
    { t:'Their day', h:'What does a <em>Tuesday</em> look like?', lede:'Daily rituals make the funniest panels.', fields:[
      { k:'feeding',    type:'single', lab:'How often does {n} eat?', opts:[['1x','🍽️ Once a day'],['2x','🍽️🍽️ Twice'],['3x','🍽️🍽️🍽️ Three times'],['grazer','∞ Constant grazer'],['schedule','⏰ Strict schedule']] },
      { k:'treats',     type:'text',   lab:"What's {ns} favourite treat?", ph:'Cheese cubes · raw carrots · my socks' },
      { k:'activities', type:'multi',  lab:'What does {n} do all day?', opts:[['nap','😴 Strategic napping'],['patrol','🚨 Window patrol'],['walks','🚶 Long walks'],['park','🌳 Park sessions'],['zoomies','💨 Zoomies'],['destroy','💥 Destroying things'],['follow','👀 Following me'],['screaming','📢 Loud opinions'],['sunbeam','☀️ Sunbeam meditation'],['tv','📺 Watching TV']] },
      { k:'sleep',      type:'single', lab:'Where does {n} sleep?', opts:[['my-bed','🛏️ My bed, obviously'],['own-bed','🐾 Their own bed'],['couch','🛋️ The couch'],['anywhere','💤 Wherever they fall'],['weird','🤷 Somewhere weird']] }
    ]},
    { t:'Their world', h:'Where do they <em>live</em>?', lede:'We set scenes in your actual world.', fields:[
      { k:'city',       type:'text',   lab:'What city do you live in?', ph:'Miami · Brooklyn · Austin' },
      { k:'home',       type:'single', lab:'What kind of home is it?', opts:[['apt','🏢 Apartment'],['house','🏡 House with yard'],['suburbs','🚙 Suburbs'],['rural','🌾 Rural / farm'],['beach','🌊 Coast'],['city','🏙️ Downtown']] },
      { k:'humans',     type:'single', lab:'Which humans live there?', opts:[['solo','🧍 Just me'],['couple','👫 Couple'],['kids','👨‍👩‍👧 Family with kids'],['roommates','🏘️ Roommates'],['multigen','👵 Multigen']] },
      { k:'squad',      type:'multi',  lab:'Any other animals at home?', opts:[['solo','👑 Only child'],['dog','🐕 Other dog(s)'],['cat','🐈 Cat(s)'],['small','🐹 Small pets'],['bird','🦜 Bird(s)'],['fish','🐠 Fish'],['exotic','🦎 Something exotic']] },
      { k:'weekend',    type:'multi',  lab:'Where do you go on weekends?', opts:[['park','🌳 Dog park'],['beach','🏖️ Beach'],['hike','🥾 Hiking'],['cafe','☕ Cafés'],['roadtrip','🚗 Road trips'],['couch','🛋️ Strictly the couch'],['travel','✈️ They travel with me']] }
    ]}
  ];

  const CORE = [
    { k:'signature', lab:'{ns} signature move', ph:'The head tilt of a thousand years, deployed for snacks…',
      ideas:['Flops belly-up mid-walk and refuses to move.','Screams like a smoke alarm when the bowl is empty.','Brings me a sock every single time I walk in the door.'] },
    { k:'iconic', lab:'{ns} finest moment', ph:'Stole an entire turkey off the table at Christmas. Zero regret…',
      ideas:['Escaped the yard and was found three blocks away, at the ice-cream shop.','Sat by the front door for six hours the day I came home from the hospital.','Won a staring contest with a raccoon. The raccoon left.'] },
    { k:'enemies', lab:'{ns} sworn enemy', ph:'Gerald the mailman. Sworn enemy since 2022…',
      ideas:['The vacuum cleaner, and the closet it lives in.','Whiskers, the smug cat next door who sits just out of reach.','The doorbell. Not a person. The sound itself.'] },
    { k:'creativeDirection', lab:"You're the director. What story should {n} star in?", ph:'Make {n} a dragon hunter in medieval times…',
      ideas:['{n} runs a tiny bakery in Paris and the whole town are regulars.','{n} is the last detective in a rainy city that never sleeps.','A gentle goodbye — tell it as one last walk together.'] }
  ];

  const DEPTH = [
    { k:'essence', lab:'Sum {n} up in one sentence', ph:"Thinks she's a person, and honestly she's winning…",
      ideas:['Sweetest creature alive — until the doorbell rings.','Grumpy on the outside, complete marshmallow underneath.','Runs this house. We just pay the mortgage.'] },
    { k:'monologue', lab:'If {n} could talk for one minute', ph:'WHERE IS MY BREAKFAST. I love you. WHERE IS MY BREAKFAST…',
      ideas:["I know I'm not allowed on the couch. I have made peace with this. I am on the couch.","You left for nine minutes. I assumed the worst. I'm so glad you're alive.",'That is my chair. It has always been my chair.'] },
    { k:'mishaps', lab:'An adventure gone wrong', ph:'Met a skunk at the park. We both lost…',
      ideas:["Ate my roommate Dani's entire birthday cake, candles included.",'Got stuck halfway through the cat flap and accepted his fate.','Rolled in something unspeakable ten minutes before the vet.'] },
    { k:'companions', lab:'{ns} sidekicks and supporting cast', ph:'Amelia, his tiny human best friend, who shares her snacks…',
      ideas:['Luna, the cat who actually runs the house.','Grandpa Joe, who visits on Sundays and brings bacon.','My neighbour Rosa, who saves him the good scraps.'] },
    { k:'oneThingToKnow', lab:'Anything else we should know?', ph:"One ear never quite stood up. It's the best thing about him…",
      ideas:["Sneak my sister Ana into a page — she's the one who picked him out.",'We adopted her the week my dad passed. She knew.','He has three legs and has never once noticed.'] },
    { k:'quirks', lab:'Anything our chef should know?', ph:'Allergic to chicken — everything else is fair game…',
      ideas:['Only drinks water if the tap is running.','Refuses any treat that is not handed over ceremonially.','Will not eat unless someone is in the room.'] }
  ];

  function E(id) { return document.getElementById(id); }

  // ── {n} = the pet's name · {ns} = the possessive. Falls back until we know it ──
  function nm(s) {
    return String(s)
      .replace(/\{ns\}/g, '<span class="petnms">your pet’s</span>')
      .replace(/\{n\}/g,  '<span class="petnm">your pet</span>');
  }
  function nmText(str) {
    const n = data.name || 'your pet';
    return String(str).replace(/\{ns\}/g, n + '’s').replace(/\{n\}/g, n);
  }
  function syncNames() {
    const n = data.name || 'your pet';
    document.querySelectorAll('#petails-wizard-scope textarea[data-phraw]').forEach(function (t) { t.placeholder = nmText(t.dataset.phraw); });
    document.querySelectorAll('#petails-wizard-scope .idea[data-raw]').forEach(function (b) { b.textContent = nmText(b.dataset.raw); });
    document.querySelectorAll('#petails-wizard-scope .petnm').forEach(function (s) { s.textContent = n; });
    document.querySelectorAll('#petails-wizard-scope .petnms').forEach(function (s) { s.textContent = n + '’s'; });
  }

  // ── progress + storage ────────────────────────────────────────────
  function saveProgress() {
    try {
      const toSave = { ...data, photos: [] };
      const uploaded = data.photos
        .filter(p => p.uploadedUrl)
        .map(p => ({ id: p.id, name: p.name, uploadedUrl: p.uploadedUrl }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        data: toSave, step: currentStep, sub: currentSub, timestamp: Date.now(),
        sid: submissionId, uploaded: uploaded, attrib: attrib
      }));
    } catch (e) { console.warn('Could not save progress', e); }
  }

  function loadProgress() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > STORAGE_TTL) { localStorage.removeItem(STORAGE_KEY); return false; }
      const keepPhotos = data.photos;
      Object.assign(data, parsed.data);
      data.photos = keepPhotos;
      // Reuse the SAME submissionId on a return visit: photos already in R2 keep
      // their home and the lead row updates instead of duplicating (contract §4).
      if (parsed.sid && /^sub_\d+_[a-z0-9]+$/i.test(parsed.sid)) submissionId = parsed.sid;
      // Photos already uploaded survive the reload (dataURLs are too big for
      // localStorage; the R2 URL is all we need to skip re-uploading them).
      if (Array.isArray(parsed.uploaded) && data.photos.length === 0) {
        data.photos = parsed.uploaded
          .filter(u => u && u.uploadedUrl)
          .map(u => ({
            id: u.id || ('restored_' + Math.random().toString(36).slice(2, 8)),
            name: u.name || 'photo.jpg', size: 0, dataUrl: null, uploadedUrl: u.uploadedUrl
          }));
      }
      // First touch wins: only adopt stored attribution when this load has none.
      if (parsed.attrib && !(attrib.utm_source || attrib.utm_campaign || attrib.fbclid)) attrib = parsed.attrib;
      // A saved handoff screen resumes on the format screen, never mid-upload.
      const s = Number(parsed.step), sb = Number(parsed.sub);
      return {
        step: (isFinite(s) && s >= 0 && s <= 3) ? s : 0,   // never resume into the build screen
        sub:  (isFinite(sb) && sb >= 0) ? sb : 0
      };
    } catch (e) { return false; }
  }

  function clearProgress() { try { localStorage.removeItem(STORAGE_KEY); } catch (e) {} }

  // ── chip groups ───────────────────────────────────────────────────
  function chipGroup(host, opts, key, multi) {
    host.innerHTML = '';
    host.className = 'chips' + (multi ? ' multi' : '');
    host.dataset.k = key;
    opts.forEach(function (o) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'chip';
      b.dataset.val = o[0];
      b.textContent = o[1];
      b.addEventListener('click', function () {
        if (multi) {
          b.classList.toggle('on');
          if (!Array.isArray(data[key])) data[key] = [];
          if (b.classList.contains('on')) data[key].push(o[0]);
          else data[key] = data[key].filter(function (x) { return x !== o[0]; });
        } else {
          Array.prototype.forEach.call(host.children, function (c) { c.classList.remove('on'); });
          b.classList.add('on');
          data[key] = o[0];
        }
        clearWizardError();
        refresh();
        saveProgress();
      });
      host.appendChild(b);
    });
  }

  chipGroup(E('species-chips'), SPECIES, 'species', false);
  chipGroup(E('occasion-chips'), OCCASION, 'occasion', false);

  // One product (3.5.0), no checkout (3.6.0): the wizard only captures the
  // submission; payment and formats live in the portal via the upgrade bridge.

  // ── screen 1: photos ──────────────────────────────────────────────
  const PHOTO_HINTS = [
    'Add at least one. Three or more and the likeness gets noticeably better.',
    'Good start. A second angle helps us get the face right.',
    'Better. One more and we can see them from every side.',
    'Great — this is enough for a strong likeness.',
    'Excellent. One more slot if you have a favourite.',
    "Perfect. That's the maximum, and the best we can work with."
  ];
  function updatePhotoHint() {
    const h = E('photo-hint');
    if (!h) return;
    const n = Math.min(data.photos.length, 5);
    h.firstChild.textContent = n + ' of 5';
    h.lastChild.textContent = PHOTO_HINTS[n];
  }

  // First tap shows the guide, then never again — the OK button is what opens the
  // file picker, so the guide costs one tap once instead of one tap every time.
  let tipsSeen = false;
  function openFilePicker() { const f = E('file-input'); if (f) f.click(); }
  function closeTips(proceed) {
    E('tips').classList.remove('on');
    tipsSeen = true;
    if (proceed) openFilePicker();
  }
  E('tips-ok').addEventListener('click', function () { closeTips(true); });
  E('tips').addEventListener('click', function (e) { if (e.target === E('tips')) closeTips(false); });
  E('tips-link').addEventListener('click', function () { E('tips').classList.add('on'); });
  E('upload-zone').addEventListener('click', function () {
    if (!tipsSeen) { E('tips').classList.add('on'); return; }
    openFilePicker();
  });

  function addThumb(photo) {
    const src = photo.dataUrl || photo.uploadedUrl;
    if (!src) return;
    const wrap = document.createElement('div');
    wrap.className = 'thumb-wrap';
    const img = document.createElement('img');
    img.className = 'photo-thumb';
    img.src = src;
    img.alt = '';
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'thumb-x';
    x.setAttribute('aria-label', 'Remove photo');
    x.textContent = '×';
    x.addEventListener('click', function (e) {
      e.stopPropagation();
      wrap.remove();
      data.photos = data.photos.filter(function (p) { return p.id !== photo.id; });
      updatePhotoHint();
      clearWizardError();
      saveProgress();
    });
    wrap.appendChild(img);
    wrap.appendChild(x);
    E('photos-preview').appendChild(wrap);
  }

  // ── screen 2: accordion blocks ────────────────────────────────────
  (function buildBlocks() {
    const host = E('blocks');
    BLOCKS.forEach(function (blk, bi) {
      const wrap = document.createElement('div');
      wrap.className = 'sub' + (bi === 0 ? ' on' : '');
      wrap.dataset.sub = bi;
      wrap.innerHTML = '<p class="eyebrow">0' + (bi + 1) + ' · ' + blk.t + '</p>' +
                       '<h2 class="wz-h">' + blk.h + '</h2>' +
                       '<p class="lede">' + blk.lede + ' <b class="block-count" data-c="' + bi + '"></b></p>';
      blk.fields.forEach(function (f) {
        const fd = document.createElement('div');
        fd.className = 'wz-field';
        const id = 'b-' + f.k;
        fd.innerHTML = '<label class="qs" for="' + id + '">' + nm(f.lab) + '</label>';
        if (f.type === 'text') {
          const inp = document.createElement('input');
          inp.type = 'text';
          inp.id = id;
          inp.placeholder = f.ph;
          inp.setAttribute('autocapitalize', 'words');
          inp.setAttribute('autocomplete', 'off');
          inp.setAttribute('autocorrect', 'off');
          inp.setAttribute('spellcheck', 'false');
          inp.setAttribute('enterkeyhint', 'next');
          inp.addEventListener('input', function () {
            data[f.k] = inp.value.trim();
            clearWizardError(); scheduleRefresh(); scheduleSave();
          });
          wireEnter(inp);
          fd.appendChild(inp);
        } else {
          const g = document.createElement('div');
          fd.appendChild(g);
          chipGroup(g, f.opts, f.k, f.type === 'multi');
        }
        wrap.appendChild(fd);
      });
      host.appendChild(wrap);
    });
  })();

  function blockState(bi) {
    let done = 0;
    BLOCKS[bi].fields.forEach(function (f) {
      const v = data[f.k];
      if (Array.isArray(v) ? v.length : (v && String(v).length)) done++;
    });
    return [done, BLOCKS[bi].fields.length];
  }


  function sparkle(head) {
    if (!head || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const marks = ['✦', '⭐', '🐾'];
    for (let i = 0; i < 6; i++) {
      (function (i) {
        const sp = document.createElement('span');
        sp.className = 'spark';
        sp.textContent = marks[i % 3];
        sp.style.right = (38 + Math.random() * 46) + 'px';
        sp.style.top = (14 + Math.random() * 16) + 'px';
        sp.style.setProperty('--dx', (Math.random() * 26 - 13).toFixed(0) + 'px');
        sp.style.animationDelay = (i * 55) + 'ms';
        head.appendChild(sp);
        setTimeout(function () { sp.remove(); }, 1500);
      })(i);
    }
  }


  // ── screen 3: the story questions ─────────────────────────────────
  function textField(host, f, required) {
    const fd = document.createElement('div');
    fd.className = 'wz-field';
    const id = 't-' + f.k;

    const lab = document.createElement('label');
    lab.className = 'q';
    lab.setAttribute('for', id);
    lab.innerHTML = nm(f.lab);
    const mark = document.createElement('small');
    if (!required) mark.className = 'opt';
    mark.textContent = required ? 'required' : 'optional';
    lab.appendChild(mark);
    fd.appendChild(lab);

    const ta = document.createElement('textarea');
    ta.id = id;
    ta.dataset.phraw = f.ph;
    ta.placeholder = nmText(f.ph);
    ta.rows = 3;
    ta.setAttribute('autocapitalize', 'sentences');
    ta.setAttribute('autocorrect', 'on');
    ta.setAttribute('spellcheck', 'true');
    ta.addEventListener('input', function () {
      data[f.k] = ta.value.trim();
      autoGrow(ta);
      clearWizardError();
      scheduleRefresh();
      scheduleSave();
    });
    fd.appendChild(ta);

    // Hidden until asked for: an idea is help when you want it and noise when
    // you already know what to write.
    if (f.ideas) {
      const ask = document.createElement('button');
      ask.type = 'button';
      ask.className = 'ideas-ask';
      ask.textContent = 'Stuck? Tap here for ideas';
      const list = document.createElement('div');
      list.className = 'ideas';
      f.ideas.forEach(function (raw) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'idea';
        row.dataset.raw = raw;
        row.textContent = nmText(raw);
        row.addEventListener('click', function () {
          const txt = nmText(raw);
          ta.value = txt;
          data[f.k] = txt;
          autoGrow(ta);
          try { ta.focus(); ta.setSelectionRange(txt.length, txt.length); } catch (e) {}
          clearWizardError();
          refresh();
          saveProgress();
          // Chosen → the list has done its job. Fold it away.
          list.classList.remove('on');
          ask.classList.remove('open');
          ask.textContent = 'Stuck? Tap here for ideas';
        });
        list.appendChild(row);
      });
      ask.addEventListener('click', function () {
        const open = list.classList.toggle('on');
        ask.classList.toggle('open', open);
        ask.textContent = open ? 'Hide ideas' : 'Stuck? Tap here for ideas';
      });
      fd.appendChild(ask);
      fd.appendChild(list);
    }

    host.appendChild(fd);
  }

  // One required question per card. Four textareas on one screen read as homework;
  // one at a time reads as a conversation, and it is the most effortful screen.
  (function buildStories() {
    const host = E('stories');
    CORE.forEach(function (f, i) {
      const wrap = document.createElement('div');
      wrap.className = 'sub' + (i === 0 ? ' on' : '');
      wrap.dataset.sub = i;
      wrap.innerHTML = '<div class="grouphead req"><b>Required</b><i>' + (i + 1) + ' of ' + CORE.length + '</i><hr></div>';
      textField(wrap, f, true);
      host.appendChild(wrap);
    });
    const opt = document.createElement('div');
    opt.className = 'sub';
    opt.dataset.sub = CORE.length;
    opt.innerHTML = '<div class="grouphead opt"><b>Optional</b><i>every extra % makes it more them</i>' +
                    '<span class="depth-gain" id="depth-gain">+20%</span><hr></div>' +
                    '<h2 class="wz-h">Want it to sound even more like <em class="petnm">your pet</em>?</h2>' +
                    '<p class="lede">Every one of these is optional — answer what makes you smile, or skip straight ahead.</p>';
    DEPTH.forEach(function (f) { textField(opt, f, false); });
    host.appendChild(opt);
  })();



  // ── the richness meter ────────────────────────────────────────────
  // Continuous, so it moves on every keystroke instead of jumping at a threshold.
  // The 4 required questions are worth 60% together, full credit at the 25-character
  // floor the validation asks for. The 6 optional ones share the remaining 40%.
  // Answered or not - no length floor. A three-word signature move is a real answer,
  // and the meter must always agree with what Next accepts (3.5.0).
  function score(k) { return (data[k] || '').trim() ? 1 : 0; }
  // The 4 required answers alone land at 80% - finishing the mandatory half has to
  // feel like a good book, not a failing grade. The 6 extras share the last 20.
  function richness() {
    let core = 0, extra = 0;
    CORE.forEach(function (f) { core += score(f.k) * (80 / CORE.length); });
    DEPTH.forEach(function (f) { extra += score(f.k) * (20 / DEPTH.length); });
    return Math.min(100, Math.round(core + extra));
  }
  // Colour ramp along the brand palette: pink → yellow at the 60% mark → sage at 100%.
  // Brand pink instead of a true alarm red — an unfinished answer is not an error.
  // pink -> light green at 80 (the required answers) -> dark green at 100.
  const STOPS = [[0, '#FF3D6E'], [80, '#95C18C'], [100, '#3E7A46']];
  function hex(h) { return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]; }
  function meterColor(p) {
    let i = 0;
    while (i < STOPS.length - 2 && p > STOPS[i + 1][0]) i++;
    const lo = STOPS[i], hi = STOPS[i + 1];
    const t = Math.max(0, Math.min(1, (p - lo[0]) / (hi[0] - lo[0])));
    const a = hex(lo[1]), b = hex(hi[1]);
    return 'rgb(' + a.map(function (v, j) { return Math.round(v + (b[j] - v) * t); }).join(',') + ')';
  }
  // One game, five stages: required unlocks, extras make it more THEM.
  const CAPS = [
    [0,  'The 4 answers ahead are what make it {n}\u2019s.'],
    [40, 'Keep going \u2014 every line makes it more {n}.'],
    [80, 'That\u2019s a book only {n} could star in \u2726'],
    [92, '100% {n}. The book people cry over.']
  ];

  // ── screen 4: vibes + style preview ───────────────────────────────
  function vibeById(id) {
    for (let i = 0; i < VIBES.length; i++) if (VIBES[i].id === id) return VIBES[i];
    return null;
  }
  // Only the tapped style loads its images — all twenty at once would stall a
  // phone on the most expensive screen of the form.
  // 3.8.0: the REAL customer photo is PINNED on the left as the reference, and only
  // the example pages scroll past it in a constant marquee (track doubled so the
  // CSS animation loops seamlessly at -50%). No labels, no swipe needed.
  function showPreview(v) {
    E('pv-name').textContent = v.name + ' Example';
    const pin = E('pv-orig');
    if (pin) {
      pin.innerHTML = '';
      pin.style.display = 'none';                 // hidden until the photo actually loads
      const ou = (v.orig || [])[0];
      if (ou) {
        const im = document.createElement('img');
        im.decoding = 'async';
        im.alt = v.name + ' — the real photo';
        im.onload = function () { pin.style.display = ''; };
        im.onerror = function () { pin.style.display = 'none'; };
        im.src = ou;
        pin.appendChild(im);
      }
    }
    const strip = E('pv-pages');
    strip.innerHTML = '';
    const urls = (v.pages || []).filter(Boolean);
    if (urls.length) {
      const track = document.createElement('div');
      track.className = 'pv-track';
      track.style.animationDuration = (urls.length * 4.5) + 's';
      urls.concat(urls).forEach(function (url) {
        const p = document.createElement('div');
        p.className = 'page';
        p.style.background = v.art;               // placeholder while the page loads
        const img = document.createElement('img');
        img.decoding = 'async';
        img.alt = v.name + ' — example page';
        img.onerror = function () { p.remove(); };  // no image, no tile — never an empty frame
        img.src = url;
        p.appendChild(img);
        track.appendChild(p);
      });
      strip.appendChild(track);
    }
    E('vibe-preview').classList.add('on');
  }
  // The panel stays where the section put it: below all five, always. It used to be
  // injected into the grid after the tapped card's row, which split the set in two -
  // on a phone that pushed the last two universes underneath the examples, so the
  // customer could no longer see what else there was to choose.
  function revealPreview(scroll) {
    if (!scroll) return;
    const pv = E('vibe-preview');
    if (pv) pv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  (function buildVibes() {
    const host = E('wizard-vibes');
    VIBES.forEach(function (v) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'vibe';
      b.dataset.vibe = v.id;
      b.innerHTML = '<span class="vibe-name">' + v.name + '</span>' +
                    '<span class="vibe-sub">' + v.sub + '</span>';
      b.addEventListener('click', function () { pickVibe(v.id, true); });
      host.appendChild(b);
    });
  })();

  function pickVibe(id, scroll) {
    const v = vibeById(id);
    if (!v) return;
    data.vibe = id;
    E('wizard-vibes').querySelectorAll('.vibe').forEach(function (c) {
      c.classList.toggle('on', c.dataset.vibe === id);
    });
    showPreview(v);
    revealPreview(!!scroll);
    clearWizardError();
    refresh();
    saveProgress();
  }

  // (newsletter row removed in 3.4.0 - consent belongs to Shopify checkout's
  //  own marketing checkbox; data.newsletter travels false unless that's wired.)


  // ── progress rail ─────────────────────────────────────────────────
  (function buildRail() {
    const rail = E('wz-rail');
    STEP_LABELS.forEach(function (s, i) {
      const d = document.createElement('div');
      d.className = 'seg';
      d.dataset.i = i;
      d.innerHTML = '<div class="seg-bar"></div><div class="seg-label">' + s + '</div>';
      rail.appendChild(d);
    });
  })();

  // ── refresh: rail, block counters, meter ──────────────────────────
  let lastR = 0;
  // ── typing ergonomics (3.10.0) ────────────────────────────────────
  // Everything that used to run synchronously on every keystroke now runs once
  // per frame (paint work) or once the typing pauses (storage). A forced reflow
  // plus a JSON.stringify + localStorage write per character is what made the
  // text arrive after the finger on a phone.
  let rafPending = 0;
  function scheduleRefresh() {
    if (rafPending) return;
    rafPending = requestAnimationFrame(function () { rafPending = 0; refresh(); });
  }
  let saveTimer = 0;
  function scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveProgress, 450);
  }
  // Deferred writes must never lose the last words: flush the moment the customer
  // leaves the field, backgrounds the tab or closes it.
  function flushSave() {
    if (!saveTimer) return;
    clearTimeout(saveTimer); saveTimer = 0;
    saveProgress();
  }
  document.addEventListener('focusout', function (e) {
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) flushSave();
  }, true);
  window.addEventListener('pagehide', flushSave);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushSave();
  });
  // A box that grows with the sentence. Fixed-height textareas scroll their own
  // content, and a scrolled textarea is where iOS loses the caret.
  const TA_MAX = 340;
  function autoGrow(ta) {
    if (!ta || ta.offsetParent === null) return;   // hidden card: scrollHeight is 0
    ta.style.height = 'auto';
    // box-sizing is border-box here, so height includes the border while
    // scrollHeight does not: without this the last line sits 4px under the edge.
    const cs = getComputedStyle(ta);
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const need = ta.scrollHeight + border;
    ta.style.height = Math.min(need, TA_MAX) + 'px';
    ta.style.overflowY = need > TA_MAX ? 'auto' : 'hidden';
  }
  function growAll() {
    document.querySelectorAll('#petails-wizard-scope textarea').forEach(autoGrow);
  }
  // Enter in a one-line field moves to the next field on the card, or closes the
  // keyboard - it never submits by accident.
  function wireEnter(el) {
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const card = el.closest('.sub') || el.closest('.screen');
      const fields = card ? card.querySelectorAll('input[type=text],input[type=email],textarea') : [];
      let next = null, seen = false;
      Array.prototype.forEach.call(fields, function (f) {
        if (seen && !next) next = f;
        if (f === el) seen = true;
      });
      if (next) { try { next.focus(); } catch (e2) {} } else el.blur();
    });
  }

  function refresh() {
    const total = subCount(currentStep);
    Array.prototype.forEach.call(E('wz-rail').children, function (d, i) {
      d.classList.toggle('done', i < currentStep);
      d.classList.toggle('now', i === currentStep);
    });
    // The current segment fills with the sub-steps, so moving inside a stage is
    // visible progress. Four cards in a row otherwise look like standing still.
    const scope = E('petails-wizard-scope');
    if (scope) scope.style.setProperty('--segPct', (currentStep < 0 ? 0 : Math.round(((currentSub + 1) / total) * 100)) + '%');

    BLOCKS.forEach(function (_, bi) {
      const st = blockState(bi);
      const c = document.querySelector('.block-count[data-c="' + bi + '"]');
      if (c) c.textContent = st[0] + '/' + st[1];
    });

    const r = richness();
    const c = meterColor(r);
    const mp = E('meter-pct');
    if (!mp) return;
    if (r > lastR) { mp.classList.remove('pop'); void mp.offsetWidth; mp.classList.add('pop'); }
    lastR = r;
    mp.textContent = r + '%';
    mp.style.color = c;
    E('meter-fill').style.width = r + '%';
    E('meter-fill').style.backgroundColor = c;
    let cap = CAPS[0][1];
    CAPS.forEach(function (x) { if (r >= x[0]) cap = x[1]; });
    E('meter-cap').textContent = nmText(cap);
    E('sec-meter').classList.toggle('passed', r >= 80);
    // The optional card's button says what it does: skip until something is typed.
    if (currentStep === 2 && currentSub === subCount(2) - 1) {
      const any = DEPTH.some(function (f) { return (data[f.k] || '').length > 0; });
      E('next-btn').textContent = any ? 'Next →' : 'Skip for now →';
    }

    // What the optional half is still worth, so the invitation is a number and
    // not an adjective. DEPTH carries 40 points; show what is left of them.
    let got = 0;
    DEPTH.forEach(function (f) { got += score(f.k) * (20 / DEPTH.length); });
    const left = Math.max(0, Math.round(20 - got));
    const gain = E('depth-gain');
    if (gain) {
      gain.textContent = left > 0 ? '+' + left + '%' : 'maxed ✦';
      gain.classList.toggle('done', left === 0);
    }
  }

  // ── navigation ────────────────────────────────────────────────────
  function measureHead() {
    const scope = E('petails-wizard-scope');
    if (!scope) return;
    // The mobile keyboard fires resize. Re-measuring then shifts scroll-margin
    // under a field the customer is typing in, which yanks the view mid-sentence.
    const a = document.activeElement;
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) return;
    const h = E('wz-head');
    if (h) scope.style.setProperty('--headH', h.offsetHeight + 'px');
    // The screen-3 meter is sticky UNDERNEATH the header, so anything scrolled to
    // on that screen has to clear both or it lands behind the meter.
    scope.style.setProperty('--meterH', '0px');   // the meter lives in the nav now
  }

  // ── sub-step engine ───────────────────────────────────────────────
  // A stage is a short series of cards, not a page you scroll. currentSub is the
  // card inside the stage; a screen with no .sub children counts as one card.
  let currentSub = 0;
  function subsOf(n) {
    const sc = document.querySelector('#petails-wizard-scope .screen[data-s="' + n + '"]');
    return sc ? sc.querySelectorAll('.sub') : [];
  }
  function subCount(n) { return Math.max(1, subsOf(n).length); }

  function showStep(n, sub) {
    clearWizardError();
    currentStep = n;
    const total = subCount(n);
    currentSub = Math.max(0, Math.min(sub || 0, total - 1));
    document.querySelectorAll('#petails-wizard-scope .screen').forEach(function (s) {
      s.classList.toggle('on', Number(s.dataset.s) === n);
    });
    Array.prototype.forEach.call(subsOf(n), function (s, i) { s.classList.toggle('on', i === currentSub); });

    const onContent = n >= 0 && n <= 4;
    E('wz-head').style.display = onContent ? '' : 'none';
    E('wizard-nav').style.display = (n >= 0 && n <= 3) ? '' : 'none';
    const mm = E('sec-meter'); if (mm) mm.classList.toggle('hidden', n !== 2);
    E('wz-stepnum').textContent = (Math.min(Math.max(n, 0), 4) + 1) + ' / ' + TOTAL_SCREENS;
    E('back-btn').style.visibility = (n <= 0 && currentSub === 0) ? 'hidden' : 'visible';
    // On the format screen the offers are the action; a second primary button
    // next to them is how you get someone to tap the wrong thing.
    E('next-btn').textContent = (n === 3 && currentSub === total - 1) ? 'Create my book →' : 'Next →';
    refresh();
    growAll();                       // a card just became visible: size its boxes
    const modal = document.querySelector('#petails-wizard-scope .modal');
    if (modal) modal.scrollTop = 0;
    measureHead();
  }

  function markMiss(el) {
    if (!el) return;
    const w = el.closest('.wz-field');
    (w || el).classList.add('miss');
  }
  function validateSub(n, sub) {
    clearWizardError();
    const shake = (el) => { if (!el) return; el.classList.add('shake'); setTimeout(() => el.classList.remove('shake'), 400); };
    const reveal = (el) => { if (!el) return; el.scrollIntoView({ behavior: 'smooth', block: 'center' }); };
    const failEl = (el, msg) => { markMiss(el); shake(el); reveal(el); showWizardError(msg); return false; };
    const failInput = (id, msg) => {
      const el = E(id); markMiss(el); shake(el); reveal(el);
      if (el) setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }, 350);
      showWizardError(msg); return false;
    };

    if (n === 0) {
      if (sub === 0 && data.photos.length === 0) return failEl(E('upload-zone'), 'Add at least one photo to start — the more you upload, the better the book ✦');
      if (sub === 1) {
        if (!data.name) return failInput('pet-name', 'Your star needs a name ✦');
        if (!data.species) return failEl(E('species-chips'), 'Pick what kind of animal they are ✦');
      }
      if (sub === 2) {
        if (!data.ownerName) return failInput('owner-name', 'What should we call you, co-author? ✦');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.ownerEmail || '')) return failInput('owner-email', 'We need a valid email to keep your progress safe ✦');
      }
    }

    if (n === 1) {
      const st = blockState(sub);
      if (st[0] < st[1]) {
        // Every unanswered field on this card gets marked, and we land on the first.
        const card = subsOf(1)[sub];
        let first = null;
        BLOCKS[sub].fields.forEach(function (f) {
          const v = data[f.k];
          if (Array.isArray(v) ? v.length : (v && String(v).length)) return;
          const el = card.querySelector('#b-' + f.k) || card.querySelector('.chips[data-k="' + f.k + '"]');
          if (el) { markMiss(el); if (!first) first = el; }
        });
        const left = st[1] - st[0];
        return failEl(first || card, left + (left === 1 ? ' answer' : ' answers') + ' to go — marked below ✦');
      }
    }

    if (n === 2) {
      const f = CORE[sub];
      if (f && !(data[f.k] || '').trim()) return failEl(E('t-' + f.k), 'Tell us this one — a few words is plenty ✦');
    }

    if (n === 3) {
      if (!data.vibe) return failEl(E('wizard-vibes'), 'Pick the universe for the book ✦');
      if (!data.occasion) return failEl(E('occasion-chips'), 'Pick an occasion — even "just love" ✦');
    }

    return true;
  }

  // ── the reward between steps ──────────────────────────────────────
  // Deliberately a toast, not a full-screen card: the old celebration blocked the
  // run and cost a tap each time. This one pays the step off and leaves by itself.
  const STEP_REWARD = [
    'A star is born <em>\u2726</em> {n}\u2019s photos are safe.',
    'We can picture {n} now <em>\ud83d\udc3e</em>',
    'That\u2019s the good stuff <em>\u2726</em> our writers will feast.',
    '{n} \u00d7 {v} <em>\u2726</em> iconic choice.'
  ];
  let toastTimer = null;
  function rewardStep(done) {
    const seg = E('wz-rail').children[done];
    if (seg) { seg.classList.remove('just'); void seg.offsetWidth; seg.classList.add('just'); }
    const el = E('wz-toast');
    if (!el || !STEP_REWARD[done]) return;
    const v = VIBE_LABEL[data.vibe] || 'their universe';
    el.innerHTML = nmText(STEP_REWARD[done]).replace('{v}', v);
    el.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('on'); }, 2600);
  }

  function nextStep() {
    if (currentStep < 0 || currentStep > 4) return;
    if (!validateSub(currentStep, currentSub)) return;
    if (currentSub < subCount(currentStep) - 1) {     // still inside this stage
      showStep(currentStep, currentSub + 1);
      saveProgress();
      return;
    }
    if (currentStep >= 4) return;
    // Leaving stage 1 files the lead and starts the photo upload in the
    // background. Nothing waits for either call (contract v1.1.0 §4).
    if (currentStep === 0) startEarlyCapture();
    // Leaving stage 4 IS the submission: photos + save_meta fire, stage 5/5 shows
    // the book being built, and checkout follows with the variant the customer
    // picked on this screen. Raffaello 2026-09-16 - knowing this starts a row
    // (pending_checkout under Apps Script v3) for unpaid forms too.
    if (currentStep === 3) {
      selectProduct();
      return;
    }
    const done = currentStep;
    showStep(currentStep + 1, 0);
    rewardStep(done);
    saveProgress();
  }

  function prevStep() {
    const t = E('wz-toast'); if (t) t.classList.remove('on');
    if (currentSub > 0) { showStep(currentStep, currentSub - 1); saveProgress(); return; }
    if (currentStep > 0) { showStep(currentStep - 1, subCount(currentStep - 1) - 1); saveProgress(); }
  }

  function showWizardError(msg) {
    const el = E('wizard-error');
    if (el) el.textContent = msg;
  }
  function clearWizardError() {
    const el = E('wizard-error');
    if (el) el.textContent = '';
    document.querySelectorAll('#petails-wizard-scope .miss').forEach(function (m) { m.classList.remove('miss'); });
  }

  // ── restoring a session ───────────────────────────────────────────
  function restoreFormUI() {
    document.querySelectorAll('#petails-wizard-scope input[data-k]').forEach(function (i) {
      if (data[i.dataset.k]) i.value = data[i.dataset.k];
    });
    BLOCKS.forEach(function (blk) {
      blk.fields.forEach(function (f) {
        if (f.type !== 'text') return;
        const el = E('b-' + f.k);
        if (el && data[f.k]) el.value = data[f.k];
      });
    });
    CORE.concat(DEPTH).forEach(function (f) {
      const el = E('t-' + f.k);
      if (el && data[f.k]) { el.value = data[f.k]; autoGrow(el); }
    });
    document.querySelectorAll('#petails-wizard-scope .chips[data-k]').forEach(function (host) {
      const v = data[host.dataset.k];
      Array.prototype.forEach.call(host.children, function (c) {
        c.classList.toggle('on', Array.isArray(v) ? v.indexOf(c.dataset.val) !== -1 : c.dataset.val === v);
      });
    });
    if (data.vibe) pickVibe(data.vibe, false);
    // Rebuild thumbnails for photos restored from a previous visit (already in R2):
    // the R2 public URL doubles as the preview image.
    const preview = E('photos-preview');
    if (preview && preview.children.length === 0 && data.photos.length) {
      data.photos.forEach(addThumb);
    }
    updatePhotoHint();
    syncNames();
  }

  function openWizard() {
    const saved = loadProgress();
    if (saved) { restoreFormUI(); showStep(saved.step, saved.sub); }
    else { showStep(-1, 0); }        // the promise, before any field
    E('wizard').classList.add('open');
    document.body.style.overflow = 'hidden';
    measureHead();
  }

  function closeWizard() {
    E('wizard').classList.remove('open');
    document.body.style.overflow = '';
  }

  // ── Hardened submission: timeouts, retries, live progress, no silent failures ──
  // 3.7.0: tightened. 4 x 25-30s attempts meant a failing backend could hold the
  // customer on "Saving your story..." for ~2 minutes before the error view -
  // which reads as "frozen" and loses the lead. Worst case is now ~60s to a
  // clear retry screen. Photos also upload early (stage-1 Next), so by submit
  // time only meta usually remains.
  const SUBMIT_DEADLINE_MS = 45000;  // whole submission, start to finish
  const HOME_DELAY_S       = 6;      // seconds on the done screen before going home
  const UPLOAD_TIMEOUT_MS = 20000;   // per attempt, per photo
  const META_TIMEOUT_MS   = 20000;
  const UPLOAD_ATTEMPTS    = 3;
  const META_ATTEMPTS      = 3;
  let isSubmitting = false;

  function fetchWithTimeout(url, options, ms) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, Object.assign({}, options, { signal: ctrl.signal })).finally(() => clearTimeout(timer));
  }

  function upStatus(msg) { const el = document.getElementById('pf-up-status'); if (el) el.textContent = msg; }
  function upProgress(done, total) { const bar = document.getElementById('pf-up-bar'); if (bar) bar.style.width = Math.round((Math.max(0, done) / Math.max(1, total)) * 100) + '%'; }
  function showUploadView(view) {
    const w = document.getElementById('pf-up-working'), er = document.getElementById('pf-up-error');
    const dn = document.getElementById('pf-up-done');
    if (w)  w.style.display  = (view === 'working') ? '' : 'none';
    if (er) er.style.display = (view === 'error')   ? '' : 'none';
    if (dn) dn.style.display = (view === 'done')    ? '' : 'none';
  }
  // The flow ends at the home page. The guard is already off by the time this runs,
  // so nothing asks the customer to confirm leaving.
  let homeTimer = 0;
  function goHome() {
    clearTimeout(homeTimer);
    setSubmitGuard(false);
    window.location.href = '/';
  }
  function showUpError(msg) { const el = document.getElementById('pf-up-errmsg'); if (el && msg) el.textContent = msg; showUploadView('error'); }

  function beforeUnloadGuard(e) { e.preventDefault(); e.returnValue = ''; return ''; }
  function setSubmitGuard(on) {
    if (on) window.addEventListener('beforeunload', beforeUnloadGuard);
    else window.removeEventListener('beforeunload', beforeUnloadGuard);
  }

  // Agrega los items al carrito vía AJAX (con submissionId como propiedad de línea)
  // y manda al checkout. Punto único: todo lo que pase por aquí queda enlazado al libro.
  // v1.1.0: fetch NO lanza en 4xx/5xx, así que verificamos la respuesta de add.js y el
  // contenido real del carrito antes de navegar; un reintento; permalink como último recurso.
  async function verifiedCartAdd(items) {
    await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    });
    var body = null;
    try { body = await res.json(); } catch (e) { body = null; }
    // /cart/add.js responde {status, message, description} cuando rechaza el add
    if (!res.ok || !body || body.status) {
      throw new Error('add-failed: ' + ((body && (body.description || body.message)) || ('HTTP ' + res.status)));
    }
    var cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
    // 3.9.0: the cart must hold EXACTLY what we just added and nothing else.
    // item_count >= 1 passed even when /cart/clear.js had failed silently, so whatever
    // had piled up in that browser travelled to checkout with the customer - and with
    // no /cart page in this store, they could not remove a single line.
    var want = items.reduce(function (n, it) { return n + (Number(it.quantity) || 1); }, 0);
    if (!cart || !cart.items || cart.item_count !== want) throw new Error('cart-dirty: expected ' + want + ', got ' + (cart && cart.item_count));
    var wanted = items.map(function (it) { return String(it.id); }).sort().join(',');
    var got = cart.items.map(function (it) { return String(it.variant_id || it.id); }).sort().join(',');
    if (wanted !== got) throw new Error('cart-dirty: unexpected variants in cart');
  }

  async function goToCheckout(productUrl) {
    var sid = getSubmissionId();
    var spec = productUrl.replace(/^\/cart\//, '').split('?')[0];
    var items = spec.split(',').map(function (pair) {
      var p = pair.split(':');
      return { id: Number(p[0]), quantity: Number(p[1] || 1), properties: { submissionId: sid } };
    });
    try {
      try {
        await verifiedCartAdd(items);
      } catch (e1) {
        console.warn('⚠️ cart add falló, reintentando:', e1);
        await new Promise(function (r) { setTimeout(r, 800); });
        await verifiedCartAdd(items);
      }
      window.location.href = '/checkout';
    } catch (e) {
      // Último recurso: permalink de carrito — llega a un checkout válido pero SIN la
      // propiedad submissionId en la línea (reconciliar a mano con el email del pedido).
      console.warn('⚠️ cart AJAX falló definitivamente; fallback permalink:', e, '· submissionId:', sid);
      window.location.href = productUrl;
    }
  }

  // 3.6.0: the wizard flow ENDS on the studio screen. No cart, no checkout, no
  // redirect - the submission is captured and everything else (unlock, payment,
  // upsell) happens by email + portal. goToCheckout/verifiedCartAdd stay below
  // untouched: the upgrade bridge (contract 4) still uses them.
  async function selectProduct() {
    if (isSubmitting) return;
    isSubmitting = true;
    setSubmitGuard(true);
    const nmEl = document.getElementById('confirm-name'); if (nmEl) nmEl.textContent = data.name || 'Your pet';
    // One clear message: everything arrives by email. Show them WHICH email.
    const emEl = document.getElementById('confirm-email'); if (emEl) emEl.textContent = data.ownerEmail || 'your email';
    showStep(4, 0);
    showUploadView('working');
    upStatus('Uploading your photos… keep this page open.');
    upProgress(data.photos.filter(p => p.uploadedUrl).length, data.photos.length || 1);

    // If the backend is slow, say so - a static line over a full bar reads as frozen.
    const slowNote = setTimeout(function () {
      if (isSubmitting) upStatus('Still working — slow connection, almost there…');
    }, 15000);

    // Hard ceiling. Photos (3 x 20s) and then meta (3 x 20s) could legitimately take
    // more than two minutes of spinner before anything was shown - which is "forever"
    // to a customer. We stop waiting at SUBMIT_DEADLINE_MS and say so honestly; the
    // request may still land, and a retry is safe because the upsert is by submissionId.
    let result;
    try {
      result = await Promise.race([
        finalizeSubmission().catch(function (e) {
          console.warn('finalizeSubmission threw:', e); return { ok: false, reason: 'exception' };
        }),
        new Promise(function (r) {
          setTimeout(function () { r({ ok: false, reason: 'deadline' }); }, SUBMIT_DEADLINE_MS);
        })
      ]);
    } catch (e) { result = { ok: false, reason: 'exception' }; }
    clearTimeout(slowNote);

    if (!result.ok) {
      isSubmitting = false;
      setSubmitGuard(false);
      if (result.reason === 'no-photos') {
        showStep(0, 0);
        showWizardError('We need your photos again — please re-add them. Your other answers are saved. ✦');
        return;
      }
      showUpError(result.reason === 'deadline'
        ? 'This is taking longer than it should and we could not confirm your story was saved. Nothing you entered is lost — tap Try again. If it already went through, you will still only get one email. ✦'
        : 'Your connection dropped before we could finish — but nothing you entered is lost. Stay on Wi-Fi if you can and tap Try again. ✦');
      return;
    }

    // success — this IS the end of the flow. isSubmitting stays true on purpose,
    // so nothing can fire a second submission from this session.
    setSubmitGuard(false);
    clearProgress();
    console.log('🐾 Submission complete:', getSubmissionId(), '· photos:', result.photoCount, '· sheet notified:', result.notified);
    // One message, no spinner. Showing "saved" while the orb kept turning is what
    // read as "it never finishes" - the done view has no moving parts at all.
    const dm = document.getElementById('done-email');
    if (dm) dm.textContent = data.ownerEmail || 'your email';
    showUploadView('done');
    let left = HOME_DELAY_S;
    const note = document.getElementById('done-redirect');
    const tick = function () {
      if (note) note.textContent = left > 0 ? ('Taking you home in ' + left + '…') : 'Taking you home…';
      if (left-- <= 0) { clearInterval(iv); goHome(); }
    };
    const iv = setInterval(tick, 1000); tick();
    homeTimer = iv;
  }

  function retrySubmit() {
    if (isSubmitting) return;
    selectProduct();
  }
  function restartFromError() {
    isSubmitting = false; setSubmitGuard(false);
    showStep(3, subCount(3) - 1);
  }

  // ── Submission helpers (hardened) ──────────────────────────────
  const WORKER_URL  = 'https://api.thepetails.com';
  // Browser-tier token, loaded from assets/petails-wizard-key.js (public by design).
  // Kept out of this file so the engine holds no credential literal.
  const PW_KEY = (typeof window !== 'undefined' && window.__PW_BROWSER_KEY) || '';
  if (!PW_KEY) console.warn('petails-wizard: browser key asset did not load — uploads will be rejected.');
  // (Sheets webhook is now fired server-side by the Worker in save_meta — no browser call.)

  let submissionId = null;
  function getSubmissionId() {
    if (!submissionId) {
      const ts = Math.floor(Date.now() / 1000).toString();
      const rand = Math.random().toString(36).substring(2, 6);
      submissionId = 'sub_' + ts + '_' + rand;
      saveProgress();   // persist the freshly minted id immediately
    }
    return submissionId;
  }

  // ── Early capture (Fase A · lead-capture contract v1.1.0) ──────
  // save_lead writes ONLY the D1 leads table server-side. It never touches the
  // sheet or the pipeline, so it can never create a book. Failure is silent to
  // the customer: a lead that does not save is our bookkeeping, not their problem.
  function sendLead() {
    try {
      const sid = getSubmissionId();
      const opts = {
        method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-Petails-Secret': PW_KEY },
        body: JSON.stringify({
          action: 'save_lead',
          submissionId: sid,
          ownerEmail: data.ownerEmail || '',
          ownerName: data.ownerName || '',
          petName: data.name || '',
          species: data.species || '',
          photoCount: data.photos.length,
          createdAt: new Date().toISOString(),
          source: attrib
        })
      };
      fetchWithTimeout(WORKER_URL, opts, 8000).catch(function () {
        setTimeout(function () { fetch(WORKER_URL, opts).catch(function () {}); }, 4000);
      });
    } catch (e) { /* lead capture must never break the form */ }
  }

  let earlyUploadRunning = false;
  function startEarlyCapture() {
    sendLead();
    if (earlyUploadRunning) return;
    if (!data.photos.some(p => !p.uploadedUrl)) return;
    earlyUploadRunning = true;
    uploadAllPhotos(getSubmissionId())
      .then(function () {
        // photo_count fresh on the lead row — but never resurrect progress that
        // selectProduct already cleared on its way to checkout.
        if (!isSubmitting) { saveProgress(); sendLead(); }
      })
      .catch(function () {})
      .finally(function () { earlyUploadRunning = false; });
  }

  function buildFormData(photoUrls) {
    return {
      ownerName: data.ownerName||'', ownerEmail: data.ownerEmail||'', occasion: data.occasion||'', source: data.source||'', newsletter: data.newsletter||false,
      vibe: ({comic:'Comic Caper',storybook:'Storybook Magic',movie:'Cinema Poster',heritage:'Heritage Edition',pixar:'Pixar Magic'})[data.vibe]||data.vibe||'',
      name: data.name||'', species: data.species||'', breed: data.breed||'', age: data.age||'', weight: data.weight||'', color: data.color||'', gender: data.gender||'',
      energy: data.energy||'', social: data.social||'', traits: data.traits||[], essence: data.essence||'', signature: data.signature||'', monologue: data.monologue||'',
      feeding: data.feeding||'', treats: data.treats||'', activities: data.activities||[], sleep: data.sleep||'', quirks: data.quirks||'',
      city: data.city||'', zip: data.zip||'', home: data.home||'', squad: data.squad||[], humans: data.humans||'', weekend: data.weekend||[],
      enemies: data.enemies||'', iconic: data.iconic||'', mishaps: data.mishaps||'', companions: data.companions||'', oneThingToKnow: data.oneThingToKnow||'',
      creativeDirection: data.creativeDirection||'', photoUrls: photoUrls, photoCount: photoUrls.length
    };
  }

  async function uploadPhotoWithRetry(photo, index, sid, attempts) {
    attempts = attempts || UPLOAD_ATTEMPTS;
    const base64 = photo.dataUrl.replace(/^data:[^;]+;base64,/, '');
    const mimeType = (photo.dataUrl.match(/data:([^;]+);base64,/) || [])[1] || 'image/jpeg';
    for (let a = 1; a <= attempts; a++) {
      try {
        const res = await fetchWithTimeout(WORKER_URL, {
          method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', 'X-Petails-Secret': PW_KEY },
          body: JSON.stringify({ action: 'upload_photo', submissionId: sid, base64, mimeType, photoIndex: index })
        }, UPLOAD_TIMEOUT_MS);
        const result = await res.json();
        if (result && result.success && result.url) return result.url;
        console.warn('⚠️ photo_' + (index + 1) + ' attempt ' + a + ' rejected:', result && result.error);
      } catch (err) {
        console.warn('⚠️ photo_' + (index + 1) + ' attempt ' + a + (err && err.name === 'AbortError' ? ' timed out' : ' error'), err);
      }
      if (a < attempts) await new Promise(r => setTimeout(r, Math.min(5000, 800 * a))); // backoff
    }
    return null;
  }

  // Uploads every pending photo IN PARALLEL (shorter wait = less chance of abandoning),
  // each independently retried with its own timeout. Updates the progress bar live.
  async function uploadAllPhotos(sid) {
    const total = data.photos.length;
    let done = data.photos.filter(p => p.uploadedUrl).length;
    upProgress(done, total);
    await Promise.all(data.photos.map(async (photo, i) => {
      if (photo.uploadedUrl) return;             // already in R2 from a prior attempt — skip
      if (!photo.dataUrl) return;                // restored placeholder without pixels — nothing to send
      const url = await uploadPhotoWithRetry(photo, i, sid);
      if (url) {
        photo.uploadedUrl = url;
        done++;
        upProgress(done, total);
        upStatus('Uploading your photos… ' + done + ' of ' + total + ' done.');
        console.log('✅ photo_' + String(i + 1).padStart(2, '0') + ' →', url);
      }
    }));
    return data.photos.filter(p => p.uploadedUrl).map(p => p.uploadedUrl);
  }

  // Writes meta.json to R2 (durable record) and asks the Worker to notify Sheets.
  // Retried with a timeout; if the Sheet mirror lags we retry to push the row, but the
  // submission is considered safe the moment meta.json is stored in R2.
  async function saveMeta(sid, formData, attempts) {
    attempts = attempts || META_ATTEMPTS;
    for (let a = 1; a <= attempts; a++) {
      try {
        const res = await fetchWithTimeout(WORKER_URL, {
          method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', 'X-Petails-Secret': PW_KEY },
          body: JSON.stringify({ action: 'save_meta', submissionId: sid, formData, notifySheets: true })
        }, META_TIMEOUT_MS);
        const result = await res.json();
        if (result && result.success) {
          // 3.7.0: success is success. meta.json is durably in R2 and the Worker
          // already retried the Sheets webhook 3x server-side - re-posting from the
          // browser cannot fix a failing webhook, it only holds the customer on the
          // saving screen. notified:false is the backend's problem to reconcile.
          if (!result.notified) console.warn('⚠️ save_meta ok but sheet NOT notified — reconcile from R2 meta.json');
          return { ok: true, notified: !!result.notified };
        }
        console.warn('⚠️ save_meta attempt ' + a + ' rejected:', result && result.error);
      } catch (err) {
        console.warn('⚠️ save_meta attempt ' + a + (err && err.name === 'AbortError' ? ' timed out' : ' error'), err);
      }
      if (a < attempts) await new Promise(r => setTimeout(r, Math.min(5000, 800 * a)));
    }
    return { ok: false, notified: false };
  }

  // ok:true ONLY when every photo is confirmed in R2 AND meta.json is saved.
  async function finalizeSubmission() {
    if (!data.photos || data.photos.length === 0) return { ok: false, reason: 'no-photos' };
    const sid = getSubmissionId();
    const total = data.photos.length;

    upStatus('Uploading your photos… keep this page open.');
    const r2Urls = await uploadAllPhotos(sid);
    if (r2Urls.length < total) {
      return { ok: false, reason: 'upload-incomplete', uploaded: r2Urls.length, total: total };
    }

    upStatus('Saving your story…');
    upProgress(total, total);
    const formData = buildFormData(r2Urls);
    const meta = await saveMeta(sid, formData);
    if (!meta.ok) return { ok: false, reason: 'meta-failed' };

    console.log('🐾 save_meta ok · sheets notified:', meta.notified);
    return { ok: true, photoCount: r2Urls.length, notified: meta.notified };
  }

  // ── Photo decoding + compression (hardened, cross-browser) ─────
  // HEIC/HEIF (iPhone default) can't be decoded by <img> outside Safari,
  // so we lazy-load heic2any and convert to JPEG first.
  let _heicLibPromise = null;
  function loadHeicLib() {
    if (window.heic2any) return Promise.resolve(window.heic2any);
    if (_heicLibPromise) return _heicLibPromise;
    _heicLibPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.async = true;
      s.onload = () => resolve(window.heic2any);
      s.onerror = () => reject(new Error('heic-lib-failed'));
      document.head.appendChild(s);
    });
    return _heicLibPromise;
  }

  function isHeic(file) {
    const t = (file.type || '').toLowerCase();
    const n = (file.name || '').toLowerCase();
    return t.indexOf('heic') !== -1 || t.indexOf('heif') !== -1 || /\.(heic|heif)$/.test(n);
  }

  // Lee la orientación EXIF (1–8) directo de los bytes del JPEG. A prueba de navegador:
  // no dependemos de que el browser auto-rote. Si no es JPEG o no tiene el tag → 1 (sin rotar).
  async function getExifOrientation(blob) {
    try {
      const buf = await blob.slice(0, 256 * 1024).arrayBuffer();
      const view = new DataView(buf);
      if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) return 1; // no es JPEG
      let offset = 2;
      while (offset + 4 <= view.byteLength) {
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xFFDA || marker === 0xFFD9) break;          // SOS / EOI → fin
        if ((marker & 0xFF00) !== 0xFF00) break;                    // no es marker → fin
        const segLen = view.getUint16(offset, false);
        if (marker === 0xFFE1) {                                    // APP1 (Exif)
          const exifStart = offset + 2;
          if (view.getUint32(exifStart, false) === 0x45786966) {    // "Exif"
            const tiff = exifStart + 6;                             // saltea "Exif\0\0"
            const little = view.getUint16(tiff, false) === 0x4949;  // 'II' = little-endian
            const g16 = (o) => view.getUint16(o, little);
            const g32 = (o) => view.getUint32(o, little);
            const ifd0 = tiff + g32(tiff + 4);
            const entries = g16(ifd0);
            for (let i = 0; i < entries; i++) {
              const entry = ifd0 + 2 + i * 12;
              if (g16(entry) === 0x0112) {                          // tag Orientation
                const v = g16(entry + 8);
                return (v >= 1 && v <= 8) ? v : 1;
              }
            }
          }
        }
        offset += segLen;                                           // saltea al próximo marker
      }
      return 1;
    } catch (e) { return 1; }
  }

  // Decodifica a píxeles CRUDOS (sin auto-orientar) para que NOSOTROS controlemos la rotación.
  // Pedir imageOrientation:'none' fuerza crudo en navegadores modernos; los viejos ya dan crudo.
  async function decodeRaw(blob) {
    if ('createImageBitmap' in window) {
      try { return await createImageBitmap(blob, { imageOrientation: 'none' }); } catch (e) {}
    }
    return await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      try { img.style.imageOrientation = 'none'; } catch (e) {}
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
      img.src = url;
    });
  }

  // Decodifica dejando que el navegador aplique la orientación EXIF (default moderno).
  async function decodeOriented(blob) {
    if ('createImageBitmap' in window) {
      try { return await createImageBitmap(blob); } catch (e) {}
    }
    return await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
      img.src = url;
    });
  }

  // Aplica la transformación EXIF al contexto del canvas. w/h = dimensiones dibujadas (escaladas).
  function applyExifTransform(ctx, orientation, w, h) {
    switch (orientation) {
      case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;             // espejo horizontal
      case 3: ctx.transform(-1, 0, 0, -1, w, h); break;            // 180°
      case 4: ctx.transform(1, 0, 0, -1, 0, h); break;             // espejo vertical
      case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;              // transpuesta
      case 6: ctx.transform(0, 1, -1, 0, h, 0); break;             // 90° CW  (vertical iPhone)
      case 7: ctx.transform(0, -1, -1, 0, h, w); break;            // transversa
      case 8: ctx.transform(0, -1, 1, 0, 0, w); break;             // 90° CCW
      default: break;                                              // 1 = sin cambios
    }
  }

  async function normalizePhoto(file) {
    const MAX_EDGE = 2048;
    const TARGET_BYTES = 1600000; // ~1.6MB cap on the base64 payload per photo
    try {
      let blob = file;
      if (isHeic(file)) {
        const heic2any = await loadHeicLib();
        let out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        blob = Array.isArray(out) ? out[0] : out;
      }

      // El navegador ya auto-orienta según EXIF (Safari 13.4+, Chrome 81+): decodificamos
      // orientado y NO aplicamos transformación manual (evita la doble rotación).
      const orientation = 1;
      const bmp = await decodeOriented(blob);
      let rw = bmp.naturalWidth || bmp.width;
      let rh = bmp.naturalHeight || bmp.height;
      if (!rw || !rh) throw new Error('zero-dimensions');

      // Escalamos la imagen cruda al lado más largo = MAX_EDGE.
      let sw = rw, sh = rh;
      if (Math.max(rw, rh) > MAX_EDGE) { const s = MAX_EDGE / Math.max(rw, rh); sw = Math.round(rw * s); sh = Math.round(rh * s); }

      // En rotaciones de 90°/270° las dimensiones del canvas se intercambian.
      const swap = orientation >= 5 && orientation <= 8;
      const canvas = document.createElement('canvas');
      canvas.width  = swap ? sh : sw;
      canvas.height = swap ? sw : sh;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';                 // aplana transparencia para JPEG
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      applyExifTransform(ctx, orientation, sw, sh);
      ctx.drawImage(bmp, 0, 0, sw, sh);
      if (bmp.close) { try { bmp.close(); } catch (e) {} }   // libera memoria del ImageBitmap

      let q = 0.9;
      let dataUrl = canvas.toDataURL('image/jpeg', q);
      while (dataUrl.length > TARGET_BYTES && q > 0.5) { q -= 0.1; dataUrl = canvas.toDataURL('image/jpeg', q); }
      return (dataUrl && dataUrl.length > 1000) ? dataUrl : null;
    } catch (e) {
      console.warn('normalizePhoto failed:', file && file.name, e);
      return null;
    }
  }

  const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif|tiff?)$/i;
  function looksLikeImage(file) {
    if (file.type && file.type.indexOf('image/') === 0) return true;
    if (isHeic(file)) return true;
    return IMAGE_EXT_RE.test(file.name || '');   // some browsers report empty type for HEIC/TIFF
  }

  async function handleFiles(files) {
    clearWizardError();
    const remaining = 5 - data.photos.length;
    if (remaining <= 0) { showWizardError('You already have 5 photos — the max. Remove one to add another. ✦'); return; }
    const list = [...files].slice(0, remaining);
    let failed = 0, heicSeen = false;
    for (const file of list) {
      if (!looksLikeImage(file)) { failed++; continue; }
      if (isHeic(file)) heicSeen = true;
      const dataUrl = await normalizePhoto(file);
      if (!dataUrl) { failed++; continue; }
      const uid = (file.name || 'photo') + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
      const photo = { id: uid, name: file.name || 'photo.jpg', size: dataUrl.length, dataUrl: dataUrl, uploadedUrl: null };
      data.photos.push(photo);
      addThumb(photo);
    }
    updatePhotoHint();
    saveProgress();
    if (failed > 0) {
      showWizardError(heicSeen
        ? 'We couldn’t process one or more photos. If they’re iPhone HEIC files and conversion failed, export them as JPG and try again. ✦'
        : 'We couldn’t process ' + failed + ' file(s). Use JPG, PNG, WEBP or HEIC. ✦');
    }
  }

  const zone = document.getElementById('upload-zone');
  ['dragenter','dragover'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.add('dragover'); }));
  ['dragleave','drop'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.remove('dragover'); }));
  zone.addEventListener('drop', e => handleFiles(e.dataTransfer.files));

  // ── landing-page hooks (the CTAs and vibe cards outside the modal) ──
  function selectVibe(card) {
    if (card.closest('#wizard')) return;
    document.querySelectorAll('.vibe-card[data-vibe]').forEach(c => { if (!c.closest('#wizard')) c.classList.remove('selected'); });
    card.classList.add('selected');
    var det = card.querySelector('.vibe-detail');
    if (det && !det.querySelector('.vibe-start')) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vibe-start';
      b.style.cssText = 'display:block;width:100%;margin-top:10px;background:var(--pink);color:#fff;font-weight:700;font-size:13px;border:2px solid var(--ink);border-radius:999px;padding:10px 14px;cursor:pointer;';
      b.textContent = 'Build my book in this universe →';
      b.addEventListener('click', function(e){
        e.stopPropagation();
        startWithVibe(card.dataset.vibe);
      });
      det.appendChild(b);
    }
  }

  function startWithVibe(v) {
    data.vibe = v;
    saveProgress();
    openWizard();
    pickVibe(v, false);
  }

  // ── legacy shims ──────────────────────────────────────────────────
  // Kept so an inline handler still sitting in the landing section cannot throw.
  // The screens they belonged to (intro, notice, celebration, quantity stepper)
  // no longer exist in v2.0.0.
  function startWizard() { showStep(0, 0); }
  function continueFromNotice() { showStep(1, 0); }
  function closeCelebrate() {}
  function changeHCQty() {}
  function buyHardcover() { selectProduct('/cart/51850659528989:1'); }
  function pickVibeWizard(card) { if (card && card.dataset && card.dataset.vibe) pickVibe(card.dataset.vibe, true); }

  // ── boot ──────────────────────────────────────────────────────────
  document.querySelectorAll('#petails-wizard-scope input[data-k]').forEach(function (i) {
    i.addEventListener('input', function () {
      data[i.dataset.k] = i.value.trim();
      if (i.dataset.k === 'name') syncNames();
      clearWizardError();
      scheduleSave();
    });
    wireEnter(i);
  });

  window.addEventListener('resize', measureHead);
  updatePhotoHint();
  syncNames();
  refresh();

  // ── Upgrade bridge ──
  // When the portal sends a customer here to upgrade a digital book to hardcover,
  // it lands as ?upgrade=<existing_submissionId>&v=<variant>. We add that variant
  // to the cart carrying the EXISTING submissionId (so the webhook upgrades the
  // right book) and go straight to checkout.
  (function () {
    if (window.__petailsUpgradeHandled) return;
    try {
      var params = new URLSearchParams(window.location.search);
      var up = params.get('upgrade');
      var v = params.get('v');
      if (up && v) {
        submissionId = up;            // force the existing book's id onto the line item
        goToCheckout('/cart/' + encodeURIComponent(v) + ':1');
      }
    } catch (e) { /* no-op */ }
  })();